import { z } from 'zod';
import { nullableDate, nullableText } from '../common.js';
import { bicSchema, ibanSchema, nullableChecked } from './employees.js';
import { PAY_RHYTHMS } from '../payroll/payroll.js';
/**
 * Lohn- und Abrechnungsdaten eines Mitarbeiters (Lohnstammdaten). Wirkia rechnet keinen Lohn ab. Es hält die Angaben bereit, die ein Lohnbüro, der Steuerberater oder
 * ein Lohnprogramm braucht, und die Vergütung mit ihrer Gültigkeit. Alles hier sieht und ändert nur, wer das Zusatzrecht `hr.payroll` hat, und jedes Ansehen steht
 * im Verlauf des Mitarbeiters (ohne Werte). Steuer-ID, Steuernummer, Sozialversicherungsnummer, Geburtsdatum und Bankverbindung stehen bei den sensiblen Daten.
 */
export const EMPLOYMENT_KINDS = ['full_time', 'part_time', 'mini_job', 'short_term', 'working_student', 'apprentice', 'intern', 'midi_job'];
export const EMPLOYMENT_KIND_LABELS = {
    full_time: 'Vollzeit',
    part_time: 'Teilzeit',
    mini_job: 'Minijob (geringfügig entlohnt)',
    short_term: 'Kurzfristig beschäftigt',
    working_student: 'Werkstudent',
    apprentice: 'Auszubildender',
    intern: 'Praktikant',
    midi_job: 'Übergangsbereich (Midijob)',
};
export const CHURCH_TAXES = ['none', 'ev', 'rk', 'ak', 'jd', 'fr', 'other'];
export const CHURCH_TAX_LABELS = {
    none: 'Keine',
    ev: 'Evangelisch',
    rk: 'Römisch-katholisch',
    ak: 'Altkatholisch',
    jd: 'Jüdische Gemeinde',
    fr: 'Freireligiöse Gemeinde',
    other: 'Sonstige',
};
export const INSURANCE_KINDS = ['statutory', 'private', 'voluntary'];
export const INSURANCE_KIND_LABELS = {
    statutory: 'Gesetzlich versichert (Pflichtmitglied)',
    voluntary: 'Gesetzlich, freiwillig versichert',
    private: 'Privat versichert',
};
export const GENDERS = ['m', 'w', 'd', 'x'];
export const GENDER_LABELS = { m: 'Männlich', w: 'Weiblich', d: 'Divers', x: 'Ohne Angabe' };
export const PAY_TYPES = ['hourly', 'flat'];
export const PAY_TYPE_LABELS = { hourly: 'Stundensatz (nach Ist-Zeit)', flat: 'Festlohn (Pauschalbetrag)' };
export const FLAT_PERIODS = ['month', 'week', 'year'];
export const FLAT_PERIOD_LABELS = { month: 'pro Monat', week: 'pro Woche', year: 'pro Jahr' };
/** Gesetzlicher Mindestlohn je Stunde in Cent, gültig ab dem Datum. Zur Orientierung, die Quelle ist die Mindestlohnkommission. */
export const MINIMUM_WAGE = [
    { from: '2025-01-01', cents: 1282 },
    { from: '2026-01-01', cents: 1390 },
    { from: '2027-01-01', cents: 1460 },
];
/** Minijob-Grenze je Monat in Cent (130 Stunden zum Mindestlohn, aufgerundet), gültig ab dem Datum */
export const MINIJOB_LIMIT = [
    { from: '2025-01-01', cents: 55600 },
    { from: '2026-01-01', cents: 60300 },
    { from: '2027-01-01', cents: 63300 },
];
const valueOn = (table, date) => [...table].reverse().find((r) => r.from <= date)?.cents ?? null;
export const minimumWageOn = (date) => valueOn(MINIMUM_WAGE, date);
export const minijobLimitOn = (date) => valueOn(MINIJOB_LIMIT, date);
/** Bezeichnungen für den Verlauf (nur die Namen der geänderten Felder, nie die Werte) */
export const PAYROLL_FIELD_LABELS = {
    birthDate: 'Geburtsdatum',
    taxNumber: 'Steuernummer',
    taxId: 'Steuer-ID',
    socialSecurityNumber: 'Sozialversicherungsnummer',
    iban: 'IBAN',
    bic: 'BIC',
    employmentKind: 'Beschäftigungsart',
    payRhythm: 'Zahlungsrhythmus',
    mainEmployment: 'Hauptbeschäftigung',
    probationUntil: 'Probezeit bis',
    contractEndsOn: 'Befristet bis',
    costCenter: 'Kostenstelle',
    taxClass: 'Steuerklasse',
    taxFactor: 'Faktor',
    childAllowance: 'Kinderfreibeträge',
    churchTax: 'Kirchensteuer',
    minijobFlatTax: 'Pauschalsteuer beim Minijob',
    insuranceKind: 'Krankenversicherung',
    healthInsuranceName: 'Krankenkasse',
    healthInsuranceNumber: 'Betriebsnummer der Krankenkasse',
    healthAdditionalBp: 'Zusatzbeitrag',
    privateSubsidyCents: 'Zuschuss zur privaten Krankenversicherung',
    childrenCount: 'Kinder (Pflegeversicherung)',
    personGroup: 'Personengruppenschlüssel',
    contributionGroup: 'Beitragsgruppenschlüssel',
    activityKey: 'Tätigkeitsschlüssel',
    rvExempt: 'Befreiung von der Rentenversicherung',
    disabled: 'Schwerbehinderung',
    gender: 'Geschlecht',
    birthName: 'Geburtsname',
    birthPlace: 'Geburtsort',
    nationality: 'Staatsangehörigkeit',
    vwlEmployeeCents: 'VWL (Arbeitnehmer)',
    vwlEmployerCents: 'VWL (Arbeitgeber)',
    pensionEmployeeCents: 'Altersvorsorge (Arbeitnehmer)',
    pensionEmployerCents: 'Altersvorsorge (Arbeitgeber)',
    garnishment: 'Pfändung',
    garnishmentNote: 'Hinweis zur Pfändung',
    note: 'Notiz',
};
const text = (max) => z.string().trim().max(max).nullable().optional().transform((v) => (v === undefined ? undefined : v ? v : null));
const digits = (n, label) => z
    .string()
    .trim()
    .nullable()
    .optional()
    .transform((v) => (v === undefined ? undefined : v ? v : null))
    .refine((v) => v == null || new RegExp(`^[0-9]{${n}}$`).test(v), `${label} hat ${n} Ziffern`);
const cents = z.number('Bitte einen Betrag angeben').int('Betrag in Cent').min(0, 'Nicht negativ').max(100_000_000).nullable().optional();
const flag = z.boolean().optional();
/** Lohnstammdaten ändern. Nicht angegebene Felder bleiben, `null` oder eine leere Eingabe löscht den Wert. */
export const updatePayrollInput = z
    .object({
    // Sensible Personendaten (liegen bei den Mitarbeiterdaten, werden aber nur hier gepflegt)
    birthDate: nullableDate,
    taxNumber: nullableText(30),
    taxId: nullableText(20),
    socialSecurityNumber: nullableText(20),
    iban: nullableChecked(ibanSchema),
    bic: nullableChecked(bicSchema),
    employmentKind: z.enum(EMPLOYMENT_KINDS).nullable().optional(),
    payRhythm: z.enum(PAY_RHYTHMS).nullable().optional(),
    mainEmployment: flag,
    probationUntil: nullableDate,
    contractEndsOn: nullableDate,
    costCenter: text(40),
    taxClass: z.number().int().min(1, 'Steuerklasse 1 bis 6').max(6, 'Steuerklasse 1 bis 6').nullable().optional(),
    taxFactor: z.number('Bitte eine Zahl angeben').gt(0, 'Der Faktor liegt zwischen 0 und 1').lt(1, 'Der Faktor liegt zwischen 0 und 1').nullable().optional(),
    childAllowance: z
        .number('Bitte eine Zahl angeben')
        .min(0)
        .max(20)
        .refine((v) => Number.isInteger(v * 2), 'In Schritten von 0,5')
        .nullable()
        .optional(),
    churchTax: z.enum(CHURCH_TAXES).nullable().optional(),
    minijobFlatTax: flag,
    insuranceKind: z.enum(INSURANCE_KINDS).nullable().optional(),
    healthInsuranceName: text(120),
    healthInsuranceNumber: digits(8, 'Die Betriebsnummer'),
    healthAdditionalBp: z.number('Bitte eine Zahl angeben').int().min(0).max(1000, 'Höchstens 10 %').nullable().optional(),
    privateSubsidyCents: cents,
    childrenCount: z.number().int().min(0).max(20).nullable().optional(),
    personGroup: digits(3, 'Der Personengruppenschlüssel'),
    contributionGroup: digits(4, 'Der Beitragsgruppenschlüssel'),
    activityKey: digits(9, 'Der Tätigkeitsschlüssel'),
    rvExempt: flag,
    disabled: flag,
    gender: z.enum(GENDERS).nullable().optional(),
    birthName: text(80),
    birthPlace: text(80),
    nationality: text(60),
    vwlEmployeeCents: cents,
    vwlEmployerCents: cents,
    pensionEmployeeCents: cents,
    pensionEmployerCents: cents,
    garnishment: flag,
    garnishmentNote: text(1000),
    note: text(2000),
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
/** Neue Vergütung: gilt ab dem Datum, der alte Eintrag bleibt im Verlauf stehen */
export const addCompensationInput = z
    .object({
    validFrom: z.iso.date('Bitte ein Datum angeben'),
    payType: z.enum(PAY_TYPES, 'Bitte Stundensatz oder Pauschalbetrag wählen'),
    /** Stundensatz je Stunde oder Pauschalbetrag je Zeitraum, in Cent */
    amountCents: z.number('Bitte einen Betrag angeben').int('Betrag in Cent').min(1, 'Der Betrag muss größer als 0 sein').max(100_000_000),
    flatPeriod: z.enum(FLAT_PERIODS).nullable().optional(),
    /** Jahressonderzahlung, zum Beispiel Weihnachts- oder Urlaubsgeld, brutto je Jahr */
    annualBonusCents: z.number().int().min(0).max(100_000_000).default(0),
    surchargeNightBp: z.number().int().min(0).max(30000, "Höchstens 300 %").default(0),
    surchargeSundayBp: z.number().int().min(0).max(30000, "Höchstens 300 %").default(0),
    surchargeHolidayBp: z.number().int().min(0).max(30000, "Höchstens 300 %").default(0),
    note: text(1000),
    /** Begründung, wenn der Stundenlohn bewusst unter dem Mindestlohn liegt (zum Beispiel Minderjährige ohne Berufsausbildung, Langzeitarbeitslose in den ersten sechs Monaten) */
    belowMinimumReason: text(300),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.payType === 'flat' && !v.flatPeriod)
        ctx.addIssue({ code: 'custom', path: ['flatPeriod'], message: 'Bitte wählen, ob der Betrag je Monat, Woche oder Jahr gilt' });
})
    .transform((v) => ({ ...v, flatPeriod: v.payType === 'flat' ? v.flatPeriod : null }));
/** Geschätzter Monatsbetrag aus einer Vergütung. Stundensatz: Wochenstunden × 52 ÷ 12. Ohne Wochenstunden bei Stundensatz null. */
export function monthlyEstimate(c, weeklyHours) {
    if (c.payType === 'hourly')
        return weeklyHours && weeklyHours > 0 ? Math.round((c.amountCents * weeklyHours * 52) / 12) : null;
    if (c.flatPeriod === 'year')
        return Math.round(c.amountCents / 12);
    if (c.flatPeriod === 'week')
        return Math.round((c.amountCents * 52) / 12);
    return c.amountCents;
}
//# sourceMappingURL=payroll.js.map